import { useEffect, useState } from 'react';

/**
 * The app's own visitor-consent bridge, published by the Perspectify browser SDK that
 * `index.html` loads. It belongs to the app's fixed embed project, never to the project
 * selected in the dashboard, so nothing here takes workspace or project scope.
 */
interface PerspectifyConsentBridge {
  openPreferences: () => void;
}

/** Dispatched by the SDK once `window.PerspectifyConsent` exists. */
export const CONSENT_READY_EVENT = 'perspectify:consent-ready';
const POLL_INTERVAL_MS = 250;
/** Older SDK builds do not dispatch the ready event; poll for them, but not forever. */
const POLL_LIMIT_MS = 30_000;

export function readConsentBridge(): PerspectifyConsentBridge | null {
  if (typeof window === 'undefined') return null;
  const bridge = (window as Window & { PerspectifyConsent?: Partial<PerspectifyConsentBridge> }).PerspectifyConsent;
  return typeof bridge?.openPreferences === 'function' ? (bridge as PerspectifyConsentBridge) : null;
}

/**
 * Whether visitor-consent preferences can be opened. False while the SDK is still loading,
 * and stays false when consent is disabled for the app or the SDK never loads (blocked,
 * offline); the entry that uses this is then simply not shown.
 */
export function usePerspectifyConsentAvailable(): boolean {
  const [available, setAvailable] = useState(() => readConsentBridge() !== null);

  useEffect(() => {
    if (available) return;
    const check = () => {
      if (readConsentBridge()) setAvailable(true);
    };
    window.addEventListener(CONSENT_READY_EVENT, check);
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    const limit = window.setTimeout(() => window.clearInterval(interval), POLL_LIMIT_MS);
    return () => {
      window.removeEventListener(CONSENT_READY_EVENT, check);
      window.clearInterval(interval);
      window.clearTimeout(limit);
    };
  }, [available]);

  return available;
}
